import { defineStore } from 'pinia'
import { ref } from 'vue'
import Network from '@/Network'
import type { MatrixSummaryRequest } from '@/Network/Types/Requests/StationRequests'
import type { MatrixSummaryResponse } from '@/Network/Types/Responses/StationResponses'
import {
  DEFAULT_MATRIX_STATE,
  type MatrixCut,
  type MatrixMetricUi
} from '@/modules/company/Dashboard/matrix/matrixState'

export const MATRIX_LOAD_ERROR = 'Não foi possível carregar a matriz'

/** Célula selecionada na Matriz (painel de detalhe). `columnKey` = `MatrixColumn.key`. */
export type MatrixSelectedCell = {
  healthUnitId: number
  sectorId: number | null
  columnKey: string
}

export const useMatrixSummaryStore = defineStore('matrixSummary', () => {
  const cut = ref<MatrixCut>(DEFAULT_MATRIX_STATE.cut)
  const metric = ref<MatrixMetricUi>(DEFAULT_MATRIX_STATE.metric)
  const data = ref<MatrixSummaryResponse | null>(null)
  /** Recorte da resposta em `data` (durante a recarga, `cut` já é o novo). */
  const dataCut = ref<MatrixCut | null>(null)
  const isLoading = ref(false)
  const loadError = ref<string | null>(null)
  const selectedCell = ref<MatrixSelectedCell | null>(null)
  // Descarta respostas atrasadas (troca rápida Mês → Semana → Horas).
  let fetchId = 0
  // Mesmo pedido já em voo (ex.: watch da tela + recarga do Index): reusa.
  let inFlight: { key: string; promise: Promise<void> } | null = null

  function fetch(filters: MatrixSummaryRequest): Promise<void> {
    const key = JSON.stringify(filters)
    if (inFlight?.key === key) return inFlight.promise
    const promise = request(filters).finally(() => {
      if (inFlight?.promise === promise) inFlight = null
    })
    inFlight = { key, promise }
    return promise
  }

  async function request(filters: MatrixSummaryRequest): Promise<void> {
    const id = ++fetchId
    // O pedido sai sempre do recorte atual (`toMatrixSummaryRequest(…, cut, …)`).
    const requestedCut = cut.value
    isLoading.value = true
    loadError.value = null
    try {
      // Sem `withInclusiveEndDate`: o BE resolve a borda do fuso (spec §3).
      const response = await Network.station.matrixSummary(filters)
      if (id === fetchId) {
        data.value = response
        dataCut.value = requestedCut
      }
    } catch {
      // fallback intencional: a tela mostra o erro com "Tentar de novo", nunca uma matriz vazia.
      if (id === fetchId) {
        data.value = null
        dataCut.value = null
        loadError.value = MATRIX_LOAD_ERROR
      }
    } finally {
      if (id === fetchId) isLoading.value = false
    }
  }

  /**
   * Só grava o recorte. A UI usa `useDashboardPeriod().setMatrixCut`, que também converte
   * a âncora (Semana ↔ Mês) e navega; chamar direto deixa a âncora do recorte anterior.
   */
  function setCut(value: MatrixCut): void {
    cut.value = value
  }

  function setMetric(value: MatrixMetricUi): void {
    metric.value = value
  }

  function select(cell: MatrixSelectedCell | null): void {
    selectedCell.value = cell
  }

  /**
   * Limpa os dados ao sair do dashboard, mas PRESERVA `cut`/`metric`: na re-navegação (sem
   * query na URL) a âncora sai do `listStationsQuery` gravado, e voltar ao recorte Mês com
   * o início de uma Semana (28/09–04/10) abriria o mês errado (outubro).
   */
  function $reset(): void {
    inFlight = null
    fetchId++
    data.value = null
    dataCut.value = null
    isLoading.value = false
    loadError.value = null
    selectedCell.value = null
  }

  return {
    cut,
    metric,
    data,
    dataCut,
    isLoading,
    loadError,
    selectedCell,
    fetch,
    setCut,
    setMetric,
    select,
    $reset
  }
})
